'use client';

import {
    Children,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
    useTransition,
} from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { Listing } from '../types';
import { Button } from '@/shared/components/ui/Button';
import { SelectPopover, SelectOption } from '@/shared/components/ui/SelectPopover';
import { StarIcon, SortIcon } from '@/shared/components/icons';
import { ListingsMap } from './ListingsMap';
import { cn } from '@/shadcn/lib/utils';
import { Label } from '@/shadcn/components/ui/label';
import { Switch } from '@/shadcn/components/ui/switch';
import { isMobile } from 'react-device-detect';
import { Pagination } from '@/shared/components/ui/Pagination';

type SortValue = 'price_asc' | 'createdAt_desc';

const SORT_OPTIONS: SelectOption<SortValue>[] = [
    { value: 'price_asc', label: 'Najlacnejšie' },
    { value: 'createdAt_desc', label: 'Najnovšie' },
];

interface Props {
    children: ReactNode;
    listings: Array<Listing>;
    pagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };

    page: number;
}

export function ListingResultsClient(props: Props) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [isPending, startTransition] = useTransition();

    const listTopRef = useRef<HTMLDivElement | null>(null);
    const paginationRef = useRef<HTMLDivElement | null>(null);

    const [view, setView] = useState<'list' | 'map'>('list');

    const [activeListingId, setActiveListingId] = useState<string | null>(null);

    const [isPaginationVisible, setIsPaginationVisible] = useState(false);

    useEffect(() => {
        window.history.scrollRestoration = 'manual';

        const el = paginationRef.current;

        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsPaginationVisible(Boolean(entry?.isIntersecting));
            },
            {
                root: null,
                threshold: 0.1,
            },
        );

        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    const sortBy = (searchParams.get('sortBy') as SortValue | null) ?? null;

    const updateUrlPage = useCallback(
        (nextPage: number) => {
            const params = new URLSearchParams(searchParams.toString());

            if (nextPage <= 1) {
                params.delete('page');
            } else {
                params.set('page', String(nextPage));
            }

            if (!isPaginationVisible) {
                listTopRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }

            startTransition(() => {
                router.push(`${pathname}?${params.toString()}`, {
                    scroll: false,
                });
            });
        },
        [pathname, router, searchParams, isPaginationVisible],
    );

    const updateUrlSort = useCallback(
        (value: SortValue) => {
            const params = new URLSearchParams(searchParams.toString());
            params.set('sortBy', value);
            params.delete('page');
            startTransition(() => {
                router.push(`${pathname}?${params.toString()}`, { scroll: false });
            });
        },
        [pathname, router, searchParams],
    );

    return (
        <div>
            <div className="!max-w-[100%]">
                <div
                    className={cn(
                        'grid items-center mt-[20px] gap-[20px]',
                        'md:gap-[0] md:grid-cols-[60fr_40fr] md:mt-[30px]',
                        '3xl:grid-cols-[50fr_50fr]',
                    )}
                >
                    <div className="cust-container !max-w-[100%] md:order-2 ">
                        <div className="flex justify-center gap-[10px]">
                            <SelectPopover
                                className="flex-1 xl:max-w-[200px]"
                                options={SORT_OPTIONS}
                                value={sortBy}
                                onChange={updateUrlSort}
                                placeholder="Zoradiť"
                                iconLeft={<SortIcon />}
                            />

                            <Button
                                className="flex-1 bg-[transparent] xl:text-[13px] xl:max-w-[200px]"
                                iconLeft={<StarIcon />}
                            >
                                Uložiť hľadanie
                            </Button>
                        </div>
                    </div>

                    <div className="cust-container !max-w-[100%] md:!pr-[0px] lg:!px-[20px] xl:!pr-[15px] 2xl:!pr-[20px] 3xl:!pr-[25px]">
                        <div className="bg-[#f6ce11] w-full rounded-[8px] flex items-center">
                            <p className="py-[8px] px-[12px] text-[13px]">
                                <b>Nájdených {props.pagination.totalItems} inzerátov</b>
                            </p>
                        </div>

                        <div className="mt-[20px] flex items-center space-x-2 lg:hidden">
                            <Switch
                                id="map-view"
                                checked={view === 'map'}
                                onCheckedChange={(checked) => {
                                    setView(checked ? 'map' : 'list');
                                }}
                            />

                            <Label htmlFor="map-view">Zobraziť na mape</Label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-[20px] grid items-start lg:grid-cols-[60fr_40fr] 3xl:grid-cols-[50fr_50fr]">
                <div className="cust-container !max-w-[100%] lg:!px-[20px] xl:!pr-[15px] 2xl:!pr-[20px] 3xl:!pr-[25px]">
                    {view === 'list' && (
                        <>
                            <div ref={listTopRef}>
                                {props.listings.length > 0 ? (
                                    <div
                                        className={cn(
                                            'grid items-stretch gap-[20px] md:grid-cols-2 md:gap-[25px]',
                                            'lg:gap-[20px]',
                                            'xl:grid-cols-3 xl:gap-[15px]',
                                            '2xl:gap-[20px]',
                                            '3xl:gap-[25px]',
                                        )}
                                        onMouseLeave={() => setActiveListingId(null)}
                                    >
                                        {Children.map(props.children, (child, idx) => {
                                            const listing = props.listings[idx];
                                            if (!listing) return null;
                                            return (
                                                <div
                                                    key={listing.id}
                                                    className="w-full h-full gap-[20px]"
                                                    onMouseEnter={() =>
                                                        setActiveListingId(listing.id)
                                                    }
                                                >
                                                    {child}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="py-[50px] text-center">
                                        <p className="font-[600]">
                                            Neboli nájdené žiadne inzeráty.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div ref={paginationRef} className="py-[50px]">
                                <Pagination
                                    page={props.page || 1}
                                    totalPages={props.pagination.totalPages || 0}
                                    onPageChange={(nextPage) => {
                                        updateUrlPage(nextPage);
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div
                    className={cn(
                        'h-[400px]',
                        'md:h-[500px]',
                        'lg:sticky lg:top-0 lg:self-start min-h-[100vh]',
                        view !== 'map' && isMobile ? 'hidden' : '',
                    )}
                >
                    <ListingsMap
                        height="100%"
                        className="lg:rounded-l-[10px] lg:rounded-r-[10px]"
                        listings={props.listings}
                        activeListingId={activeListingId}
                        showLoadMore={props.page < props.pagination.totalPages}
                        onLoadMore={() => updateUrlPage(props.page + 1)}
                    />
                </div>
            </div>
        </div>
    );
}
